import {
	type GhostTextDataV2,
	type GhostTextRotation,
	ROTATION_THRESHOLD,
} from '../../../../common/utils/quick-find/ghost-text';

/**
 * Select the appropriate ghost text based on occurrence count.
 * Each text is shown 3 times before rotating to the next.
 *
 * @param rotations - Ordered list of text rotations
 * @param locale - Locale for the ghost text
 * @param existingGhostTextData - Existing ghost text data to determine current state
 * @returns Object with selected text and updated occurrence
 */
export const selectGhostText = (
	rotations: GhostTextRotation[],
	locale: string,
	existingGhostTextData?: GhostTextDataV2,
): GhostTextDataV2 => {
	if (rotations.length === 0) {
		return { id: '', locale, text: '', occurrence: 0 };
	}

	// Find current position by id
	let currentIndex = 0;
	let currentOccurrence = 0;
	if (existingGhostTextData) {
		const foundIndex = rotations.findIndex((r) => r.id === existingGhostTextData.id);
		if (foundIndex >= 0) {
			currentIndex = foundIndex;
			currentOccurrence = existingGhostTextData.occurrence ?? 0;
		}
	}

	// Check if we need to rotate to next text
	const newOccurrence = currentOccurrence + 1;
	if (newOccurrence >= ROTATION_THRESHOLD) {
		// Move to next text, reset occurrence
		const nextIndex = (currentIndex + 1) % rotations.length;
		return {
			id: rotations[nextIndex].id,
			locale,
			text: rotations[nextIndex].text,
			occurrence: 0,
		};
	}

	// Keep current text, increment occurrence
	return {
		id: rotations[currentIndex].id,
		locale,
		text: rotations[currentIndex].text,
		occurrence: newOccurrence,
	};
};
